import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FeatureFlag } from '@/config/featureFlags';
import {
  clearFlagStorage,
  DEMO_CLOSED_LOOP_FLAGS,
  DEFAULT_FLAGS,
  resolveFeatureFlags,
  writeFlagStorage,
} from '@/config/featureFlags';

type Ctx = {
  flags: Record<FeatureFlag, boolean>;
  isEnabled: (flag: FeatureFlag) => boolean;
  setFlag: (flag: FeatureFlag, on: boolean) => void;
  setMany: (partial: Partial<Record<FeatureFlag, boolean>>) => void;
  enableDemoClosedLoop: () => void;
  resetToDefaults: () => void;
};

const FeatureFlagContext = createContext<Ctx | null>(null);

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<Record<FeatureFlag, boolean>>(() => resolveFeatureFlags());

  const persist = useCallback((next: Record<FeatureFlag, boolean>) => {
    setFlags(next);
    const overlay: Partial<Record<FeatureFlag, boolean>> = {};
    (Object.keys(DEFAULT_FLAGS) as FeatureFlag[]).forEach((k) => {
      if (next[k] !== DEFAULT_FLAGS[k]) overlay[k] = next[k];
    });
    writeFlagStorage(overlay);
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      flags,
      isEnabled: (flag) => !!flags[flag],
      setFlag: (flag, on) => persist({ ...flags, [flag]: on }),
      setMany: (partial) => persist({ ...flags, ...partial }),
      enableDemoClosedLoop: () => {
        const next = { ...DEFAULT_FLAGS };
        DEMO_CLOSED_LOOP_FLAGS.forEach((k) => {
          next[k] = true;
        });
        persist(next);
      },
      resetToDefaults: () => {
        clearFlagStorage();
        setFlags(resolveFeatureFlags({}));
      },
    }),
    [flags, persist]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

// Hooks co-located with provider (shared context binding)
/* eslint-disable react-refresh/only-export-components */
export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  return ctx;
}

export function useFeature(flag: FeatureFlag): boolean {
  return useFeatureFlags().isEnabled(flag);
}
/* eslint-enable react-refresh/only-export-components */
